/**
 * Utility functions for serializing and deserializing chips to/from URL parameters
 * Format: chipType:id:value (value is optional)
 * Example: ?chip=3:123:Department%20A&chip=2:456&chip=1:0:C
 */

/**
 * Serialize chips array to URL parameter array format
 * @param {Array} chips - Array of chip objects {chipType, id, value}
 * @returns {Array} Array of serialized chip strings
 */
export const serializeChipsToParams = (chips) => {
	if (!Array.isArray(chips)) return [];
	
	return chips.map(chip => {
		const parts = [chip.chipType, chip.id];
		if (chip.value && chip.value.trim() !== '') {
			parts.push(encodeURIComponent(chip.value));
		}
		return parts.join(':');
	});
};

/**
 * Deserialize URL parameters to chips array
 * @param {Array} chipParams - Array of chip parameter strings
 * @returns {Array} Array of chip objects {chipType, id, value}
 */
export const deserializeChipsFromParams = (chipParams) => {
	if (!Array.isArray(chipParams)) return [];
	
	return chipParams.map(param => {
		const parts = param.split(':');
		if (parts.length < 2) return null; // Invalid format
		
		const chipType = parseInt(parts[0], 10);
		const id = isNaN(parseInt(parts[1], 10)) ? parts[1] : parseInt(parts[1], 10);
		
		// Rejoin remaining parts for value (in case value contains colons)
		const encodedValue = parts.length > 2 ? parts.slice(2).join(':') : '';
		
		return {
			chipType,
			id,
			value: encodedValue ? decodeURIComponent(encodedValue) : ""
		};
	}).filter(chip => chip && !isNaN(chip.chipType)); // Filter out invalid chips
};

/**
 * Add chips to URL parameters
 * @param {URLSearchParams} urlParams - URLSearchParams object
 * @param {Array} chips - Array of chip objects
 */
export const addChipsToUrlParams = (urlParams, chips) => {
	// Remove existing chip parameters
	urlParams.delete('chip');
	
	// Add new chip parameters
	const serializedChips = serializeChipsToParams(chips);
	serializedChips.forEach(chipParam => {
		urlParams.append('chip', chipParam);
	});
};

/**
 * Extract chips from URL parameters
 * @param {URLSearchParams} urlParams - URLSearchParams object
 * @returns {Array} Array of chip objects
 */
export const extractChipsFromUrlParams = (urlParams) => {
	const chipParams = urlParams.getAll('chip');
	return deserializeChipsFromParams(chipParams);
};